// Single source of truth for what we teach, by year level.
//
// Every course is presented on this site. `external` records that a program
// is *delivered* on csonlineschool.com.au, which matters for enrolment, but
// it must never send a visitor away mid-browse — they came here to find out
// what we teach, so all links stay internal.
//
// Year 11-12 are not offered yet — add them here and every page that reads
// this file (course finder, Courses menu, /our-courses) picks them up.

export const CSONLINE_BASE = "https://csonlineschool.com.au";

export const STAGES = {
  primary: "Primary",
  high: "High School",
  senior: "Senior (HSC)",
};

export const CATALOGUE = [
  {
    year: 1,
    stage: "primary",
    subjects: [{ name: "WRMT", label: "WRMT", slug: "wrmt", external: true }],
  },
  {
    year: 2,
    stage: "primary",
    subjects: [{ name: "WRMT", label: "WRMT", slug: "wrmt", external: true }],
  },
  {
    year: 3,
    stage: "primary",
    subjects: [
      { name: "WRMT", label: "WRMT", slug: "wrmt", external: true },
      { name: "OC Preliminary Trial Tests", slug: "oc-preliminary-trial-tests", external: true },
    ],
  },
  {
    year: 4,
    stage: "primary",
    subjects: [
      { name: "WRMT", label: "WRMT", slug: "wrmt", external: true },
      { name: "OC Trial Tests", slug: "oc-trial-tests", external: true },
    ],
  },
  {
    year: 5,
    stage: "primary",
    subjects: [
      { name: "WRMT", label: "WRMT", slug: "wrmt", external: true },
      { name: "Selective Preliminary/Trial Tests", slug: "selective-preliminary-trial-tests", external: true },
      { name: "HD Class", slug: "hd-class", external: false },
    ],
  },
  // Year 6 is the irregular year (NSW): each course runs only part of the
  // year, so its entries say when (see PERIODS). In Term 4 Year 6 starts the
  // high school subjects a term early as the Y6/7 classes; those three
  // together are the High School Preparation course.
  {
    year: 6,
    stage: "primary",
    plan: "NSW",
    subjects: [
      { name: "WRMT", label: "WRMT", slug: "wrmt", external: true, when: ["t1"] },
      {
        name: "Selective Trial Tests",
        slug: "selective-trial-tests",
        external: true,
        when: ["summer", "t1", "autumn", "t2"],
      },
      { name: "WEM", label: "WEM", slug: "wem", external: true, when: ["t2", "t3"] },
      {
        name: "High School Preparation",
        slug: "high-school-preparation",
        external: false,
        when: ["t4"],
        parts: [
          { name: "English & Writing", slug: "english-and-writing" },
          { name: "Mathematics", slug: "maths" },
          { name: "Science", slug: "science" },
        ],
      },
    ],
  },
  {
    year: 7,
    stage: "high",
    subjects: [
      { name: "English & Writing", slug: "english-and-writing", external: false },
      { name: "Maths", slug: "maths", external: false },
      { name: "Science", slug: "science", external: false },
    ],
  },
  {
    year: 8,
    stage: "high",
    subjects: [
      { name: "English & Writing", slug: "english-and-writing", external: false },
      { name: "Maths", slug: "maths", external: false },
      { name: "Science", slug: "science", external: false },
    ],
  },
  {
    year: 9,
    stage: "high",
    subjects: [
      { name: "English & Writing", slug: "english-and-writing", external: false },
      { name: "Maths", slug: "maths", external: false },
      { name: "Science", slug: "science", external: false },
    ],
  },
  {
    year: 10,
    stage: "high",
    subjects: [
      { name: "English & Writing", slug: "english-and-writing", external: false },
      { name: "Maths", slug: "maths", external: false },
      { name: "Science", slug: "science", external: false },
    ],
  },
  {
    year: 11,
    stage: "senior",
    subjects: [
      { name: "English", slug: "hsc-english", external: false },
      { name: "Maths Advanced (2U)", slug: "maths-advanced", external: false },
      { name: "Maths Extension 1 (3U)", slug: "maths-extension-1", external: false },
      { name: "Physics", slug: "physics", external: false },
      { name: "Chemistry", slug: "chemistry", external: false },
      { name: "Biology", slug: "biology", external: false },
    ],
  },
  {
    year: 12,
    stage: "senior",
    subjects: [
      { name: "English", slug: "hsc-english", external: false },
      { name: "Maths Advanced (2U)", slug: "maths-advanced", external: false },
      { name: "Maths Extension 1 (3U)", slug: "maths-extension-1", external: false },
      { name: "Maths Extension 2 (4U)", slug: "maths-extension-2", external: false },
      { name: "Physics", slug: "physics", external: false },
      { name: "Chemistry", slug: "chemistry", external: false },
      { name: "Biology", slug: "biology", external: false },
    ],
  },
];

// The school year in order, holidays between the terms. A course's `when`
// lists the keys it runs in.
export const PERIODS = [
  { key: "summer", letter: "S", short: "Sum", label: "Summer holiday", holiday: true },
  { key: "t1", letter: "1", short: "T1", label: "Term 1" },
  { key: "autumn", letter: "A", short: "Aut", label: "Autumn holiday", holiday: true },
  { key: "t2", letter: "2", short: "T2", label: "Term 2" },
  { key: "winter", letter: "W", short: "Win", label: "Winter holiday", holiday: true },
  { key: "t3", letter: "3", short: "T3", label: "Term 3" },
  { key: "spring", letter: "S", short: "Spr", label: "Spring holiday", holiday: true },
  { key: "t4", letter: "4", short: "T4", label: "Term 4" },
];

// "Term 2 and Term 3", "Summer holiday, Term 1, Autumn holiday and Term 2".
export const whenText = (when) => {
  const labels = PERIODS.filter((p) => (when || []).includes(p.key)).map((p) => p.label);
  if (labels.length <= 1) return labels[0] || "";
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
};

// The courses of a year level that run in a given period (a PERIODS key).
// Courses without `when` run all year. When nothing runs in that period (a
// holiday with no holiday course), the next period that has something is
// used instead. Returns { subjects, period } with the period actually shown,
// or every course with period null when there is no period to go by.
export const runningIn = (year, periodKey) => {
  const subjects = findYear(year)?.subjects || [];
  const at = PERIODS.findIndex((p) => p.key === periodKey);
  if (at < 0 || !subjects.some((sub) => sub.when?.length)) return { subjects, period: null };
  for (let i = 0; i < PERIODS.length; i += 1) {
    const period = PERIODS[(at + i) % PERIODS.length];
    const now = subjects.filter((sub) => !sub.when?.length || sub.when.includes(period.key));
    if (now.length) return { subjects: now, period };
  }
  return { subjects, period: null };
};

// A year level's course, with its `when` and `parts` if it has them.
export const subjectFor = (year, slug) =>
  findYear(year)?.subjects.find((x) => x.slug === slug) || null;

export const yearsForStage = (stage) =>
  CATALOGUE.filter((y) => y.stage === stage);

export const findYear = (year) =>
  CATALOGUE.find((y) => String(y.year) === String(year));

// Every distinct subject on offer, for the "any year" case in the finder.
export const ALL_SUBJECTS = [
  ...new Set(CATALOGUE.flatMap((y) => y.subjects.map((s) => s.name))),
].sort();

// Years that teach a given subject.
export const yearsTeaching = (subject) =>
  CATALOGUE.filter((y) => y.subjects.some((s) => s.name === subject));

// Where a given year+subject should send the visitor. Always our own site.
export const linkFor = (year, subject) => {
  const entry = findYear(year);
  const match = entry?.subjects.find((s) => s.name === subject);
  if (!match) return null;
  return { href: `/courses/year-${year}/${match.slug}`, external: false };
};

// The distinct programs we run, each with the years it is offered in.
// Derived from CATALOGUE so there is still only one list to maintain.
export const PROGRAMS = (() => {
  const by = new Map();
  for (const y of CATALOGUE) {
    for (const sub of y.subjects) {
      if (!by.has(sub.slug)) {
        by.set(sub.slug, {
          slug: sub.slug,
          name: sub.name,
          external: sub.external,
          years: [],
        });
      }
      by.get(sub.slug).years.push(y.year);
    }
  }
  return [...by.values()];
})();

export const findProgram = (slug) => PROGRAMS.find((p) => p.slug === slug);

// Holiday programs are not run for every course.
//
// They are daily rather than weekly — a week or two of morning and afternoon
// sessions, ten lessons either way — and in practice only the primary years
// fill them. From Year 6 the only holiday course that runs is the selective
// trial test, which is sat in the year it prepares for.
export const holidayAvailable = (year, slug) => {
  const y = Number(year);
  if (String(slug || "").startsWith("selective")) return y >= 5;
  return y >= 1 && y <= 5;
};

// What a course is called at a given year level, as on that year's
// timetable. The comprehensive primary course is WRMT (Writing, Reading
// skills, Mathematical reasoning skills and Thinking skills); Year 6 takes it
// in Term 1 and then WEM (Writing, English and Mathematics) in Terms 2-3.
// (It was WEMG in Years 1-3, with General Ability; that name is gone.)
export const labelFor = (year, slug) => {
  const sub = findYear(year)?.subjects.find((x) => x.slug === slug);
  return sub?.label || sub?.name || "";
};

// What the course acronyms stand for. Many families don't know them, so a
// page that shows one says what it means as well.
export const ACRONYMS = {
  WRMT: "Writing, Reading skills, Mathematical reasoning skills and Thinking skills",
  WEM: "Writing, English and Mathematics",
  WEMG: "Writing, English, Mathematics and General ability",
};

// The acronym in a course name or label ("Year 1/2 WRMT", "WEM"), or "".
export const acronymIn = (name) => {
  const m = String(name || "").match(/\b(WRMT|WEMG|WEM)\b/);
  return m ? m[1] : "";
};

// What the acronym in a course name stands for, or "" when it has none.
export const spellOut = (name) => ACRONYMS[acronymIn(name)] || "";

// "WRMT: Writing, ..." for each different acronym among the labels, for a
// note under a list of courses.
export const acronymNotes = (labels) =>
  [...new Set(labels.map(acronymIn).filter(Boolean))].map((a) => `${a}: ${ACRONYMS[a]}`);
